import React, { useState, useEffect, useRef } from 'react';
import { useSocket } from '../../hooks/useSocket';
import { useAudio } from '../../hooks/useAudio';
import { useAlertContext } from '../../context/AlertContext';
import { chatAPI } from '../../services/api';
import MessageInput from './MessageInput';
import ChatMessage from './ChatMessage';
import UserDropdown from './UserDropdown';
import ParticipantDropdown from './ParticipantDropdown';

const ChatRoom = ({ sessionId, name, onLeave }) => {
	const {
		connected,
		connectionStatus,
		messages,
		groupInfo,
		participants,
		moderatorThinking,
		confirmedAudioMessages,
		isIdle,
		sendMessage,
		callModerator,
		leaveRoom,
		handleUserActive,
	} = useSocket(sessionId);

	const {
		audioOutputEnabled,
		audioInputEnabled,
		recordedAudioBlob,
		currentlyPlayingMessageId,
		isAwaitingDecision,
		manualAnchorMessageId,
		toggleAudioOutput,
		toggleAudioInput,
		playAudio,
		pauseAudio,
		clearAudioQueue,
		continueFrom,
		skipToLatest,
		startRecording,
		stopRecording,
		sendRecording,
		deleteRecording,
		isRecording,
		isUploading,
	} = useAudio(sessionId);

	const [isRequestingOutput, setIsRequestingOutput] = useState(false);
	const [thinkingDots, setThinkingDots] = useState(0);
	const chatContainerRef = useRef(null);
	const { showAlert } = useAlertContext();
	const prevConfirmedAudioRef = useRef(new Set());

	// Auto-scroll to bottom when messages change
	useEffect(() => {
		// Use setTimeout to ensure DOM has been updated
		setTimeout(() => {
			window.scrollTo(0, document.body.scrollHeight);
		}, 0);
	}, [messages]);

	// Auto-scroll when moderator thinking indicator appears
	useEffect(() => {
		if (!moderatorThinking) return;
		setTimeout(() => {
			window.scrollTo(0, document.body.scrollHeight);
		}, 0);
	}, [moderatorThinking]);

	// Animate ellipsis while moderator is thinking
	useEffect(() => {
		if (!moderatorThinking) {
			setThinkingDots(0);
			return;
		}
		const id = setInterval(() => {
			setThinkingDots((prev) => (prev + 1) % 4);
		}, 450);
		return () => clearInterval(id);
	}, [moderatorThinking]);

	// Set initial confirmed audio when audioOutputEnabled becomes true for the first time
	useEffect(() => {
		if (audioOutputEnabled && prevConfirmedAudioRef.current.size === 0) {
			prevConfirmedAudioRef.current = new Set(confirmedAudioMessages);
		}
	}, [audioOutputEnabled, confirmedAudioMessages]);

	// Auto-play only NEW confirmed audio messages (after audio output is enabled)
	useEffect(() => {
		if (!audioOutputEnabled || confirmedAudioMessages.size === 0) {
			return;
		}

		// Find newly confirmed audio messages
		const newlyConfirmedMessages = [...confirmedAudioMessages].filter(
			(messageId) => !prevConfirmedAudioRef.current.has(messageId)
		);

		if (newlyConfirmedMessages.length > 0) {
			// Find the corresponding message objects and auto-play them
			newlyConfirmedMessages.forEach((messageId) => {
				const message = messages.find(
					(msg) => msg.message_id && msg.message_id.toString() === messageId
				);

				if (message && message.type !== 'system') {
					const audioUrl = `${
						process.env.REACT_APP_BASE_URL || '/'
					}api/messages/audio?group_id=${message.group_id}&message_id=${
						message.message_id
					}`;

					// Use isAutoPlay = true to always add to queue
					playAudio(audioUrl, message.message_id, true);
				}
			});

			// Update the ref with all current confirmed messages
			prevConfirmedAudioRef.current = new Set(confirmedAudioMessages);
		}
	}, [confirmedAudioMessages, audioOutputEnabled, playAudio, messages]);

	// Clear audio queue on component unmount
	useEffect(() => {
		return () => {
			clearAudioQueue();
		};
	}, [clearAudioQueue]);

	const handleSendMessage = (text) => {
		if (!text.trim()) return;

		if (connected) {
			sendMessage(text);
		} else {
			showAlert(
				'Not connected to chat server. Please try refreshing the page.',
				'danger'
			);
		}
	};

	const handleSendAudio = async (audioBlob) => {
		if (!connected) {
			showAlert(
				'Not connected to chat server. Please try refreshing the page.',
				'danger'
			);
			return;
		}

		try {
			await sendRecording(audioBlob);
		} catch (error) {
			console.error('Error sending audio:', error);
			showAlert('Failed to send audio message.', 'danger');
		}
	};

	const handleDeleteRecording = () => {
		deleteRecording();
	};

	const handleCallModerator = () => {
		if (connected) {
			callModerator();
			showAlert('Moderator has been called.', 'info');
		} else {
			showAlert(
				'Not connected to chat server. Please try refreshing the page.',
				'danger'
			);
		}
	};

	const handleLeaveRoom = () => {
		leaveRoom();
		if (onLeave) {
			onLeave();
		}
	};

	const handleRequestOutput = async () => {
		setIsRequestingOutput(true);

		try {
			const response = await chatAPI.requestOutput(sessionId);

			if (response.error) {
				showAlert(response.error, 'danger');
			} else {
				showAlert(
					response.message || 'Output request sent successfully',
					'success'
				);
			}
		} catch (error) {
			console.error('Error requesting output:', error);
			showAlert('Failed to request output', 'danger');
		} finally {
			setIsRequestingOutput(false);
		}
	};

	// Handle manual play button clicks (isAutoPlay = false by default)
	const handleManualPlayAudio = (audioURL, messageId) => {
		playAudio(audioURL, messageId, false);
	};

	// Helpers
	// Eligible for autoplay (confirmed audio only)
	const isEligibleForAudio = (msg) => {
		if (!msg || msg.type === 'system') return false;
		if (!msg.message_id) return false;
		return confirmedAudioMessages.has(msg.message_id.toString());
	};

	// Playable for manual continuation (do not require confirmed audio)
	const isPlayableForManual = (msg) => {
		if (!msg || msg.type === 'system') return false;
		return Boolean(msg.message_id);
	};

	const buildAudioUrl = (msg) => {
		return `${
			process.env.REACT_APP_BASE_URL || '/'
		}api/messages/audio?group_id=${msg.group_id}&message_id=${msg.message_id}`;
	};

	const handleContinueFromNext = () => {
		if (!manualAnchorMessageId) {
			return;
		}
		const anchorIndex = messages.findIndex(
			(m) => m && m.message_id === manualAnchorMessageId
		);
		if (anchorIndex === -1) {
			return;
		}
		let nextItem = null;
		for (let i = anchorIndex + 1; i < messages.length; i += 1) {
			const msg = messages[i];
			if (isPlayableForManual(msg)) {
				nextItem = { url: buildAudioUrl(msg), messageId: msg.message_id };
				break;
			}
		}
		if (nextItem) {
			continueFrom(nextItem);
		} else {
			// No next item; close decision state gracefully
			skipToLatest(null);
		}
	};

	const handleSkipToLatest = () => {
		let latest = null;
		for (let i = messages.length - 1; i >= 0; i -= 1) {
			const msg = messages[i];
			if (isPlayableForManual(msg)) {
				latest = { url: buildAudioUrl(msg), messageId: msg.message_id };
				break;
			}
		}
		skipToLatest(latest);
	};

	// Prompt user when manual playback finishes
	useEffect(() => {
		if (!isAwaitingDecision) return;
		const ok = window.confirm(
			'Playback finished. Click OK to skip to latest, or Cancel to continue from next.'
		);
		if (ok) {
			handleSkipToLatest();
		} else {
			handleContinueFromNext();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAwaitingDecision]);

	const getConnectionStatusStyle = (status) => {
		if (status === 'Connected') {
			return { backgroundColor: '#e6f7e6' }; // Light green
		}
		if (status.startsWith('Disconnected') || status.startsWith('Failed')) {
			return { backgroundColor: '#f8d7da' }; // Light red
		}
		// For connecting, reconnecting etc.
		return { backgroundColor: '#fff3cd' }; // Light yellow
	};

	return (
		<div id="chatRoom" style={{ display: 'block' }}>
			<div id="status" style={{ display: 'none' }}></div>
			<div id="alert-container"></div>

			{/* Sticky Header with Participant Dropdown, Group Info, and User Dropdown */}
			<div className="sticky-top bg-white shadow-sm">
				<div className="col-12 d-flex justify-content-between align-items-center border-bottom px-3">
					{/* Left: Participant Dropdown */}
					<div className="d-flex align-items-center">
						{groupInfo ? (
							<ParticipantDropdown
								participants={participants}
								currentUsername={
									name || sessionStorage.getItem('chat_username') || 'User'
								}
							/>
						) : (
							<div style={{ width: '60px' }}></div>
						)}
					</div>

					{/* Center: Group Info and Topic */}
					<div className="flex-grow-1 text-center">
						{groupInfo ? (
							<div className="d-flex align-items-center justify-content-center">
								<span
									className="mr-3"
									style={{
										display: 'inline-flex',
										alignItems: 'center',
										justifyContent: 'center',
										width: '32px',
										height: '32px',
										borderRadius: '50%',
										backgroundColor: '#000',
										color: '#fff',
										fontWeight: 700,
										fontSize: '0.95rem',
									}}
									aria-label="Group number"
								>
									{groupInfo.group_id}
								</span>
								<span
									className="text-dark"
									style={{ fontSize: '1.15rem', fontWeight: 600 }}
								>
									{groupInfo.topic}
								</span>
							</div>
						) : (
							<div className="text-muted">
								<span
									className="spinner-border spinner-border-sm mr-2"
									role="status"
									aria-hidden="true"
								></span>
								Connecting to chat...
							</div>
						)}
					</div>

					{/* Right: User Dropdown */}
					<div className="d-flex align-items-center">
						<UserDropdown
							username={
								name || sessionStorage.getItem('chat_username') || 'User'
							}
							isIdle={isIdle}
							audioOutputEnabled={audioOutputEnabled}
							audioInputEnabled={audioInputEnabled}
							toggleAudioOutput={toggleAudioOutput}
							toggleAudioInput={toggleAudioInput}
							onRequestOutput={handleRequestOutput}
							onLeaveRoom={handleLeaveRoom}
							isRequestingOutput={isRequestingOutput}
						/>
					</div>
				</div>
				{/* Connection Status Bar */}
				{!connected && connectionStatus && (
					<div
						className="connection-status-bar text-center small p-1"
						style={getConnectionStatusStyle(connectionStatus)}
					>
						<span>{connectionStatus}</span>
					</div>
				)}
			</div>

			<div id="chat-area">
				<div className="d-flex flex-column h-100">
					{/* Decision prompt handled via window.confirm */}
					<div
						id="chat"
						className="flex-grow-1 overflow-auto"
						ref={chatContainerRef}
					>
						{messages.map((message, index) => (
							<ChatMessage
								key={index}
								message={message}
								currentUsername={
									name || sessionStorage.getItem('chat_username')
								}
								onPlayAudio={handleManualPlayAudio}
								onPauseAudio={pauseAudio}
								audioEnabled={audioOutputEnabled}
								isCurrentlyPlaying={
									currentlyPlayingMessageId === message.message_id
								}
							/>
						))}
						{moderatorThinking && (
							<div
								className="text-muted small px-3 py-2"
								style={{ opacity: 0.9 }}
							>
								Moderator is thinking{'.'.repeat(thinkingDots)}
							</div>
						)}
					</div>

					<MessageInput
						onSendMessage={handleSendMessage}
						onSendAudio={handleSendAudio}
						audioInputEnabled={audioInputEnabled}
						sessionId={sessionId}
						startRecording={startRecording}
						stopRecording={stopRecording}
						isRecording={isRecording}
						isUploading={isUploading}
						recordedAudioBlob={recordedAudioBlob}
						onDeleteRecording={handleDeleteRecording}
					/>
				</div>
			</div>
		</div>
	);
};

export default ChatRoom;
